/* eslint-disable react/prop-types */
/**
 * FeedbackFormModal — "Report a problem / request a feature".
 *
 * Opened from the user menu in the nav bar (alongside Theme and Sign Out), so
 * it is reachable from anywhere in the app without navigating away from
 * whatever just broke. Open to every authenticated user, internal or external.
 *
 * WHAT MAKES THIS WORTH BUILDING is not the text box — it is everything the
 * form collects WITHOUT asking. On open it force-records the Microsoft
 * Clarity session (Clarity samples sessions, and the one you most want to
 * watch is the one where something went wrong); on submit it attaches the
 * browser timestamp, timezone, Clarity session/page ids, page URL, app
 * version, user agent and viewport. An admin can then go watch the failure
 * instead of asking "what were you doing?".
 *
 * The context block is shown to the reporter, collapsed, rather than
 * collected silently. Someone filing a bug from a page full of deal terms is
 * entitled to see what the app is about to send along with it.
 *
 * SCREENSHOTS are uploaded AFTER the ticket exists, because the upload
 * endpoint is nested under the ticket id. That ordering has a consequence
 * worth knowing: an attachment failure cannot roll back the report. We keep
 * the ticket, tell the user which files didn't make it, and let them retry
 * from the ticket — losing a written-out bug report because an image upload
 * timed out would be the worse outcome by far.
 */
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import {
  Alert,
  Button,
  Collapse,
  Descriptions,
  Form,
  Input,
  Modal,
  Radio,
  Select,
  Space,
  Typography,
  Upload,
  message,
} from "antd";
import {
  BugOutlined,
  BulbOutlined,
  InboxOutlined,
  QuestionCircleOutlined,
} from "@ant-design/icons";
import { useLocation } from "react-router";

import { AppContext } from "../AppContextProvider";
import appSupportService from "../services/appSupportService";
import {
  captureClientContext,
  upgradeClaritySession,
} from "../utils/clientContext";
import { apiErrorText } from "../utils/apiError";

const { Text, Paragraph } = Typography;
const { TextArea } = Input;

const KINDS = [
  { value: "bug", label: "Something is broken", icon: <BugOutlined /> },
  { value: "feature", label: "I'd like a feature", icon: <BulbOutlined /> },
  { value: "question", label: "I have a question", icon: <QuestionCircleOutlined /> },
];

const SEVERITIES = [
  { value: "low", label: "Low — cosmetic or minor annoyance" },
  { value: "medium", label: "Medium — I can work around it" },
  { value: "high", label: "High — blocks part of my work" },
  { value: "critical", label: "Critical — I can't do my job" },
];

// Keep this in step with the server's 25 MB per-file cap so an oversized
// screenshot is refused here, before it is uploaded and 413'd.
const MAX_FILE_BYTES = 25 * 1024 * 1024;

const FeedbackFormModal = ({ open, onClose, onSubmitted }) => {
  const [form] = Form.useForm();
  const location = useLocation();
  const { token, getToken, isTokenExpired, user } = useContext(AppContext);

  const [submitting, setSubmitting] = useState(false);
  const [kind, setKind] = useState("bug");
  const [files, setFiles] = useState([]);
  // The diagnostic block, captured on open so it describes the page the user
  // was ON when they hit the problem — not the modal they are now looking at,
  // and not a page they may navigate to while typing.
  const [context, setContext] = useState(null);

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const getAuth = useCallback(async () => {
    const userOid = user?.idTokenClaims?.oid || user?.localAccountId;
    let tk = token;
    if (!tk || isTokenExpired(tk)) tk = await getToken();
    return { token: tk, userOid };
  }, [token, getToken, isTokenExpired, user]);

  useEffect(() => {
    if (!open) return;
    // Force Clarity to keep this session. Must happen on OPEN: the useful part
    // of the recording is what the user did BEFORE reaching for this form.
    upgradeClaritySession("feedback-report");

    let cancelled = false;
    (async () => {
      const ctx = await captureClientContext({
        userEmail: user?.idTokenClaims?.preferred_username || user?.username,
        route: location?.pathname,
      });
      if (!cancelled && mountedRef.current) setContext(ctx);
    })();
    return () => {
      cancelled = true;
    };
  }, [open, user, location?.pathname]);

  const resetAndClose = useCallback(() => {
    form.resetFields();
    setFiles([]);
    setKind("bug");
    setContext(null);
    onClose?.();
  }, [form, onClose]);

  const handleSubmit = useCallback(async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return; // AntD renders the field errors.
    }

    setSubmitting(true);
    try {
      const { token: tk, userOid } = await getAuth();
      const resp = await appSupportService.createFeedback({
        token: tk,
        userOid,
        kind: values.kind,
        title: values.title,
        description: values.description,
        // Severity only means anything on a bug; sending it on a feature
        // request would put a meaningless "critical" on the admin's queue.
        severity: values.kind === "bug" ? values.severity : undefined,
        context,
      });

      const created = resp?.data?.data;
      const requestId = created?.id;

      // Attachments go up after the ticket exists (the endpoint is nested
      // under its id). A failure here does NOT discard the report.
      const failed = [];
      if (requestId && files.length) {
        for (const f of files) {
          try {
            await appSupportService.uploadFeedbackAttachment({
              token: tk,
              userOid,
              id: requestId,
              file: f.originFileObj || f,
            });
          } catch (err) {
            console.error("Feedback attachment upload failed:", err?.message || err);
            failed.push(f.name);
          }
        }
      }

      if (failed.length) {
        message.warning(
          `Report #${requestId} was sent, but ${failed.length} attachment(s) ` +
            `didn't upload (${failed.join(", ")}). You can add them from ` +
            `My Requests.`
        );
      } else {
        message.success(
          requestId
            ? `Thanks — report #${requestId} is with the team.`
            : "Thanks — your report is with the team."
        );
      }

      onSubmitted?.(created);
      resetAndClose();
    } catch (err) {
      console.error("Failed to submit feedback:", err?.message || err);
      message.error(
        apiErrorText(err, { fallback: "Couldn't send that report." })
      );
    } finally {
      if (mountedRef.current) setSubmitting(false);
    }
  }, [form, getAuth, context, files, onSubmitted, resetAndClose]);

  const contextRows = context
    ? [
        ["Page", context.page_url],
        ["Reported at", context.client_reported_at],
        [
          "Your timezone",
          context.client_timezone
            ? `${context.client_timezone} (UTC${
                context.client_tz_offset_minutes >= 0 ? "+" : ""
              }${(context.client_tz_offset_minutes ?? 0) / 60})`
            : null,
        ],
        ["App version", context.app_version],
        ["Screen", context.viewport],
        ["Session recording", context.clarity_session_id],
      ].filter(([, v]) => v)
    : [];

  return (
    <Modal
      open={open}
      title="Send feedback"
      onCancel={resetAndClose}
      // antd 6: `maskClosable` is deprecated in favour of `mask.closable`.
      // Don't let a stray backdrop click discard a typed-out bug report.
      mask={{ closable: false }}
      width={640}
      footer={[
        <Button key="cancel" onClick={resetAndClose} disabled={submitting}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={submitting}
          onClick={handleSubmit}
        >
          Send
        </Button>,
      ]}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{ kind: "bug", severity: "medium" }}
        onValuesChange={(changed) => {
          if (changed.kind) setKind(changed.kind);
        }}
      >
        <Form.Item name="kind" label="What's this about?">
          <Radio.Group
            optionType="button"
            buttonStyle="solid"
            options={KINDS.map((k) => ({
              value: k.value,
              label: (
                <Space size={6}>
                  {k.icon}
                  {k.label}
                </Space>
              ),
            }))}
          />
        </Form.Item>

        <Form.Item
          name="title"
          label="One-line summary"
          rules={[
            { required: true, message: "A short summary, please." },
            { whitespace: true, message: "A short summary, please." },
          ]}
        >
          <Input
            maxLength={140}
            showCount
            placeholder={
              kind === "bug"
                ? "e.g. Deals grid goes blank after filtering by status"
                : "e.g. Let me export the deals grid to Excel"
            }
          />
        </Form.Item>

        <Form.Item
          name="description"
          label={kind === "bug" ? "What happened?" : "What would help?"}
          rules={[
            { required: true, message: "A few words of detail, please." },
            { whitespace: true, message: "A few words of detail, please." },
          ]}
          extra={
            kind === "bug"
              ? "What you did, what you expected, what happened instead."
              : undefined
          }
        >
          <TextArea rows={5} maxLength={4000} showCount />
        </Form.Item>

        {kind === "bug" && (
          <Form.Item name="severity" label="How badly does this hurt?">
            <Select options={SEVERITIES} />
          </Form.Item>
        )}

        <Form.Item label="Screenshots (optional)">
          <Upload.Dragger
            multiple
            // Hold the files locally and upload them after the ticket is
            // created — the upload endpoint is nested under the ticket id.
            beforeUpload={(file) => {
              if (file.size > MAX_FILE_BYTES) {
                message.error(
                  `${file.name} is larger than the 25 MB limit.`
                );
                return Upload.LIST_IGNORE;
              }
              return false;
            }}
            fileList={files}
            onChange={({ fileList }) => setFiles(fileList)}
            accept="image/*,.pdf,.txt,.log,.csv,.xlsx"
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">
              Drag a screenshot here, or click to choose
            </p>
            <p className="ant-upload-hint">
              A picture of the problem is worth a paragraph describing it.
            </p>
          </Upload.Dragger>
        </Form.Item>

        {contextRows.length > 0 && (
          <Collapse
            size="small"
            ghost
            items={[
              {
                key: "ctx",
                label: (
                  <Text type="secondary">
                    We&apos;ll also send some technical details
                  </Text>
                ),
                children: (
                  <>
                    <Paragraph type="secondary" style={{ fontSize: 12 }}>
                      This helps us find your session recording and reproduce
                      the problem. It goes only to the app admins.
                    </Paragraph>
                    <Descriptions
                      size="small"
                      column={1}
                      items={contextRows.map(([label, value]) => ({
                        key: label,
                        label,
                        children: (
                          <Text
                            type="secondary"
                            style={{ fontSize: 12, wordBreak: "break-all" }}
                          >
                            {value}
                          </Text>
                        ),
                      }))}
                    />
                  </>
                ),
              },
            ]}
          />
        )}

        {context && !context.clarity_session_id && (
          <Alert
            type="info"
            showIcon
            style={{ marginTop: 8 }}
            title="No session recording available"
            description={
              "Session replay isn't running in this browser, so please " +
              "describe the steps that led up to the problem."
            }
          />
        )}
      </Form>
    </Modal>
  );
};

export default FeedbackFormModal;
